<template>
  <div class="trainer-radar-plot" :style="{ height: `${height}px` }">
    <svg viewBox="0 0 640 440" preserveAspectRatio="xMidYMid meet" role="img" :aria-label="ariaLabel">
      <g>
        <polygon
          v-for="ring in rings"
          :key="`ring-${ring.level}`"
          :points="ring.points"
          :fill="ring.level % 2 === 0 ? 'rgba(148, 163, 184, 0.035)' : 'rgba(56, 189, 248, 0.018)'"
          stroke="rgba(148, 163, 184, 0.24)"
          stroke-width="1.4" />

        <line
          v-for="axis in axes"
          :key="`line-${axis.index}`"
          :x1="centerX"
          :y1="centerY"
          :x2="axis.outer.x"
          :y2="axis.outer.y"
          stroke="rgba(148, 163, 184, 0.2)"
          stroke-width="1.2" />

        <polygon
          v-if="hasValues"
          :points="dataPolygon"
          :fill="color"
          fill-opacity="0.2"
          :stroke="color"
          stroke-width="3"
          stroke-linejoin="round" />

        <g v-for="marker in visibleMarkers" :key="`marker-${marker.index}`">
          <circle :cx="marker.point.x" :cy="marker.point.y" r="5" :fill="color" stroke="#111827" stroke-width="2">
            <title>{{ marker.label }}: {{ formatRate(marker.value) }}</title>
          </circle>
          <text
            :x="marker.valueLabel.x"
            :y="marker.valueLabel.y"
            :text-anchor="marker.textAnchor"
            dominant-baseline="middle"
            class="trainer-radar-plot__value">
            {{ formatRoundedRate(marker.value) }}
          </text>
        </g>

        <text
          v-for="axis in axes"
          :key="`label-${axis.index}`"
          :x="axis.labelPoint.x"
          :y="axis.labelPoint.y"
          :text-anchor="axis.textAnchor"
          dominant-baseline="middle"
          class="trainer-radar-plot__label">
          {{ axis.label }}
        </text>
      </g>
    </svg>
  </div>
</template>

<script>
const CENTER_X = 320
const CENTER_Y = 220
const RADIUS = 150
const LABEL_RADIUS = 184

function polarPoint(angle, radius) {
  return {
    x: Number((CENTER_X + Math.cos(angle) * radius).toFixed(2)),
    y: Number((CENTER_Y + Math.sin(angle) * radius).toFixed(2)),
  }
}

function pointsAttribute(points) {
  return points.map((point) => `${point.x},${point.y}`).join(" ")
}

export default {
  name: "TrainerRadarPlot",
  props: {
    labels: { type: Array, default: () => [] },
    values: { type: Array, default: () => [] },
    max: { type: Number, default: 100 },
    height: { type: Number, default: 440 },
    color: { type: String, default: "#38bdf8" },
    ariaLabel: { type: String, default: "Spinnennetz der Tätigkeitsanteile" },
  },
  computed: {
    centerX() {
      return CENTER_X
    },
    centerY() {
      return CENTER_Y
    },
    maximum() {
      return Math.max(1, Number(this.max || 0))
    },
    entries() {
      return (Array.isArray(this.labels) ? this.labels : []).map((label, index) => ({
        index,
        label: String(label || ""),
        value: Math.max(0, Number(this.values?.[index] || 0)),
      }))
    },
    axes() {
      const count = this.entries.length
      if (count < 3) return []
      return this.entries.map((entry, index) => {
        const angle = -Math.PI / 2 + (Math.PI * 2 * index) / count
        const horizontalDirection = Math.cos(angle)
        return {
          ...entry,
          angle,
          outer: polarPoint(angle, RADIUS),
          labelPoint: polarPoint(angle, LABEL_RADIUS),
          textAnchor: horizontalDirection > 0.22 ? "start" : horizontalDirection < -0.22 ? "end" : "middle",
        }
      })
    },
    rings() {
      return [5, 4, 3, 2, 1].map((level) => ({
        level,
        points: pointsAttribute(this.axes.map((axis) => polarPoint(axis.angle, RADIUS * (level / 5)))),
      }))
    },
    markers() {
      return this.axes.map((axis) => {
        const ratio = Math.min(axis.value, this.maximum) / this.maximum
        const point = polarPoint(axis.angle, RADIUS * ratio)
        const valueLabel = polarPoint(axis.angle, RADIUS * ratio + 15)
        const horizontalDirection = Math.cos(axis.angle)
        return {
          ...axis,
          point,
          valueLabel,
          textAnchor: horizontalDirection > 0.18 ? "start" : horizontalDirection < -0.18 ? "end" : "middle",
        }
      })
    },
    visibleMarkers() {
      return this.markers.filter((marker) => marker.value > 0)
    },
    dataPolygon() {
      return pointsAttribute(this.markers.map((marker) => marker.point))
    },
    hasValues() {
      return this.visibleMarkers.length > 0
    },
  },
  methods: {
    formatRate(value) {
      return `${Number(value || 0).toFixed(1)}%`
    },
    formatRoundedRate(value) {
      return `${Math.round(Number(value || 0))}%`
    },
  },
}
</script>

<style scoped>
.trainer-radar-plot {
  display: grid;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  place-items: center;
}

.trainer-radar-plot svg {
  display: block;
  width: 100%;
  height: 100%;
}

.trainer-radar-plot__label {
  fill: #dbe7f5;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
}

.trainer-radar-plot__value {
  fill: #f8fafc;
  font-family: inherit;
  font-size: 12px;
  font-weight: 900;
  paint-order: stroke;
  stroke: #0f172a;
  stroke-width: 4px;
  stroke-linejoin: round;
}

@media (max-width: 600px) {
  .trainer-radar-plot__label {
    font-size: 11px;
  }

  .trainer-radar-plot__value {
    font-size: 10px;
  }
}
</style>
